import { Helmet } from 'react-helmet';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useSEO } from '@/hooks/useSEO';
import { ImageMetadata } from './SEO';

interface StructuredDataProps {
  images?: ImageMetadata[];
  pageType?: 'home' | 'menu' | 'about' | 'contact' | 'blog' | 'default';
}

const StructuredData = ({ images = [], pageType = 'default' }: StructuredDataProps) => {
  const { contactInfo, socialMedia } = useSiteSettings();
  const { seoData } = useSEO();
  
  const sameAs = [];
  if (socialMedia.tiktok) sameAs.push(socialMedia.tiktok);
  if (socialMedia.instagram) sameAs.push(socialMedia.instagram);
  if (socialMedia.facebook) sameAs.push(socialMedia.facebook);
  if (socialMedia.twitter) sameAs.push(socialMedia.twitter);

  // Determine schema type based on SEO settings
  const schemaType = seoData?.schema_type || "Restaurant";
  
  const baseSchema = {
    "@context": "https://schema.org",
    "name": "Junnior's Tacos Mexican Restaurant",
    "image": "https://junniorstacos.com/og-image.jpg",
    "description": "Family-owned Mexican restaurant serving authentic homemade recipes. From our food truck Junnior's Tacos y Más to a full restaurant experience.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": contactInfo.address.split(',')[0] || "727 Bankhead Hwy",
      "addressLocality": "Carrollton",
      "addressRegion": "GA",
      "postalCode": "30117",
      "addressCountry": "US"
    },
    "telephone": contactInfo.phone.replace(/[^0-9+]/g, ''),
    "email": contactInfo.email,
    "sameAs": sameAs
  };

  // Restaurant-specific schema
  const restaurantSchema = {
    ...baseSchema,
    "@type": "Restaurant",
    "servesCuisine": "Mexican",
    "priceRange": "$$",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.5801,
      "longitude": -85.0766
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "11:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Friday", "Saturday"],
        "opens": "11:00",
        "closes": "22:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "21:00"
      }
    ]
  };

  // LocalBusiness schema
  const localBusinessSchema = {
    ...baseSchema,
    "@type": "LocalBusiness",
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 33.5801,
      "longitude": -85.0766
    }
  };

  // Menu schema
  const menuSchema = {
    "@context": "https://schema.org",
    "@type": "Menu",
    "name": "Junnior's Tacos Menu",
    "description": "Authentic Mexican cuisine menu featuring tacos, burritos, enchiladas, and more",
    "inLanguage": "en-US"
  };

  // Determine which schema to use
  let schema;
  switch (schemaType) {
    case 'LocalBusiness':
      schema = localBusinessSchema;
      break;
    case 'Menu':
      schema = menuSchema;
      break;
    case 'Restaurant':
    default:
      schema = restaurantSchema;
      break;
  }

  // Generate ImageObject schemas for all images
  const siteUrl = 'https://junniorstacos.com';
  const imageSchemas = images.map((img, idx) => {
    const fullUrl = img.url.startsWith('http') ? img.url : `${siteUrl}${img.url}`;
    return {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "@id": `${siteUrl}#image-${idx}`,
      "name": img.alt || `Junnior's Tacos Mexican Restaurant Image ${idx + 1}`,
      "contentUrl": fullUrl,
      "url": fullUrl,
      "description": img.alt || "Authentic Mexican food from Junnior's Tacos in Carrollton, GA",
      "author": {
        "@type": "Organization",
        "name": "Junnior's Tacos Mexican Restaurant"
      },
      ...(img.width && { "width": `${img.width}px` }),
      ...(img.height && { "height": `${img.height}px` }),
      "inLanguage": "en-US"
    };
  });

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      
      {/* Individual image schemas */}
      {imageSchemas.map((imgSchema, idx) => (
        <script key={`img-schema-${idx}`} type="application/ld+json">
          {JSON.stringify(imgSchema)}
        </script>
      ))}
    </Helmet>
  );
};

export default StructuredData;
